import { useState } from "react";
import { Compass, PenTool, Code2, Rocket, Check, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { cn } from "@/lib/utils";

const STEPS = [
  {
    icon: Compass,
    number: "01",
    phase: "Phase 1",
    title: "Strategie & Express-Briefing",
    timeline: "Tag 1",
    badge: "⚡ Blitzstart",
    badgeColor: "text-blue-400 bg-blue-400/10 border-blue-400/30",
    glowColor: "shadow-blue-500/20",
    accentColor: "text-blue-400",
    description:
      "In einem strukturierten Express-Briefing analysieren wir Ihre Zielgruppe, prüfen Ihre Corporate Identity und entwickeln einen klaren Projektplan — alles an einem einzigen Tag.",
    points: [
      "Kostenfreies Erstgespräch & Zielgruppenfokus",
      "CI-Check & Markenpositionierung",
      "Klare Projektplanung & Festpreis-Angebot",
      "Keyword-Recherche für Ihre Region",
    ],
  },
  {
    icon: PenTool,
    number: "02",
    phase: "Phase 2",
    title: "Individuelles High-End UI/UX Design",
    timeline: "Tag 2–4",
    badge: "🎨 Kein Baukasten",
    badgeColor: "text-violet-400 bg-violet-400/10 border-violet-400/30",
    glowColor: "shadow-violet-500/20",
    accentColor: "text-violet-400",
    description:
      "Kein Template, kein Baukasten-Look. Innerhalb von 3 Tagen erhalten Sie ein pixelgenaues Screendesign in Ihrem Branding — mit direkter Feedbackschleife und sofortiger Korrektur.",
    points: [
      "Individuelles Screendesign, pixelgenau",
      "Kein Baukasten — echtes Custom Design",
      "Direkte Feedbackschleife & schnelle Korrekturen",
      "Mobile-First Designprinzip von Anfang an",
    ],
  },
  {
    icon: Code2,
    number: "03",
    phase: "Phase 3",
    title: "Performance-Code & Lokales SEO",
    timeline: "Tag 5–6",
    badge: "🚀 < 1s Ladezeit",
    badgeColor: "text-[#C5A059] bg-[#C5A059]/10 border-[#C5A059]/30",
    glowColor: "shadow-[#C5A059]/20",
    accentColor: "text-[#C5A059]",
    description:
      "Sauberer, blitzschneller Code nach modernen Webstandards. Ladezeiten unter 1 Sekunde, vollständige Responsiveness auf allen Endgeräten und lokale SEO-Grundlagen für Ihre Region.",
    points: [
      "Ladezeiten unter 1s — Google PageSpeed 98+",
      "Saubere Responsiveness auf allen Geräten",
      "Lokales SEO & Google Business Setup",
      "DSGVO-konform von Anfang an",
    ],
  },
  {
    icon: Rocket,
    number: "04",
    phase: "Phase 4",
    title: "Go-Live & Lead-Generierung",
    timeline: "Tag 7",
    badge: "✅ Go-Live",
    badgeColor: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
    glowColor: "shadow-emerald-500/20",
    accentColor: "text-emerald-400",
    description:
      "Finales Testing, Domainaufschaltung und Go-Live — Ihre Website ist bereit, Neukunden zu gewinnen. Wir bleiben mit laufender Betreuung und Optimierung an Ihrer Seite.",
    points: [
      "Vollständiges Testing & QA vor Go-Live",
      "Domainaufschaltung & Hosting-Setup",
      "Nahtlose Übergabe & Einweisung",
      "Laufende Betreuung & Wachstums-Monitoring",
    ],
  },
];

export const Process = () => {
  const [active, setActive] = useState(0);
  const [mobileActive, setMobileActive] = useState(0);
  const step = STEPS[active] || STEPS[0];

  return (
    <section
      id="konzept"
      data-testid="process-section"
      className="relative border-t border-white/10 bg-[#0A0A0A] py-24 lg:py-32 overflow-hidden"
    >
      {/* Subtle background glow */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div
          className="absolute -top-32 left-1/2 h-[500px] w-[700px] -translate-x-1/2 rounded-full"
          style={{ background: "radial-gradient(circle, rgba(197, 160, 89, 0.05) 0%, transparent 70%)" }}
        />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mb-16 max-w-2xl lg:mb-20">
            <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#C5A059]">
              KONZEPT & VORGEHEN
            </p>
            <h2 className="font-heading text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
              Vom Erstgespräch zum fertigen{" "}
              <span className="text-[#C5A059]">Webauftritt in Rekordzeit.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              Keine monatelangen Wartezeiten. Deine maßgeschneiderte Website steht in{" "}
              <strong className="text-white font-semibold">7 bis 14 Tagen</strong> — strukturiert, transparent und zu einem klaren Festpreis.
            </p>
          </div>
        </Reveal>

        {/* Desktop: Horizontal stepper + detail panel */}
        <div className="hidden lg:block">
          {/* Step selector - horizontal */}
          <Reveal>
            <div className="relative mb-10 flex items-center gap-0">
              {STEPS.map((s, i) => (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`process-step-${i + 1}`}
                  aria-pressed={active === i}
                  className={cn(
                    "group relative flex flex-1 flex-col items-center gap-3 px-4 pb-8 pt-2 text-center transition-all duration-300 outline-none",
                  )}
                >
                  {/* Connector line */}
                  {i < STEPS.length - 1 && (
                    <div className="absolute right-0 top-[28px] h-px w-1/2 translate-x-1/2 bg-white/10" />
                  )}
                  {i > 0 && (
                    <div className="absolute left-0 top-[28px] h-px w-1/2 -translate-x-1/2 bg-white/10" />
                  )}

                  {/* Icon circle */}
                  <div
                    className={cn(
                      "relative z-10 flex h-14 w-14 items-center justify-center rounded-full border-2 transition-all duration-300",
                      active === i
                        ? "border-[#C5A059] bg-[#C5A059]/15 shadow-lg shadow-[#C5A059]/25"
                        : "border-white/15 bg-[#121212] group-hover:border-white/30"
                    )}
                  >
                    <s.icon
                      className={cn(
                        "h-5 w-5 transition-colors duration-300",
                        active === i ? "text-[#C5A059]" : "text-muted-foreground/50 group-hover:text-muted-foreground"
                      )}
                    />
                  </div>

                  <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-300">
                    {s.phase}
                  </span>
                  <span
                    className={cn(
                      "font-heading text-sm font-semibold transition-colors duration-300",
                      active === i ? "text-white" : "text-muted-foreground group-hover:text-white/70"
                    )}
                  >
                    {s.title}
                  </span>
                  <span
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-[10px] font-semibold transition-all duration-300",
                      active === i ? s.badgeColor : "border-white/20 text-neutral-300 bg-white/5"
                    )}
                  >
                    {s.timeline}
                  </span>

                  {/* Active indicator dot */}
                  <div
                    className={cn(
                      "absolute bottom-0 h-0.5 w-full rounded-full bg-[#C5A059] transition-all duration-500",
                      active === i ? "opacity-100" : "opacity-0"
                    )}
                  />
                </button>
              ))}
            </div>
          </Reveal>

          {/* Detail panel */}
          <Reveal delay={100}>
            <div
              key={active}
              data-testid="process-detail-panel"
              className={cn(
                "rounded-2xl border border-white/10 bg-[#121212] p-10 shadow-xl transition-all duration-300",
                step.glowColor
              )}
            >
              <div className="grid grid-cols-[1fr,auto] gap-8 items-start">
                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div
                      className={cn(
                        "flex h-14 w-14 items-center justify-center rounded-xl border",
                        step.badgeColor
                      )}
                    >
                      <step.icon className={cn("h-7 w-7", step.accentColor)} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <span className={cn("text-xs font-semibold uppercase tracking-wider", step.accentColor)}>
                          {step.phase}
                        </span>
                        <span className={cn("rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider", step.badgeColor)}>
                          {step.badge}
                        </span>
                      </div>
                      <h3 className="font-heading text-2xl font-semibold text-white tracking-tight">
                        {step.title}
                      </h3>
                    </div>
                  </div>
                  <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">
                    {step.description}
                  </p>
                </div>
                <div className="hidden xl:block" aria-hidden="true">
                  <span className={cn("font-heading text-7xl font-light opacity-20 select-none", step.accentColor)}>
                    {step.number}
                  </span>
                </div>
              </div>

              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {step.points.map((point) => (
                  <div
                    key={point}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3.5 transition-colors hover:border-white/20"
                  >
                    <Check className={cn("h-4 w-4 shrink-0", step.accentColor)} />
                    <span className="text-sm text-foreground/85">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* Mobile: Vertical glow cards */}
        <div className="flex flex-col gap-5 lg:hidden">
          {STEPS.map((s, i) => {
            const isOpen = mobileActive === i;
            return (
              <Reveal key={s.title} delay={i * 80}>
                <div
                  className={cn(
                    "relative rounded-2xl border bg-[#121212] p-6 transition-all duration-300",
                    isOpen
                      ? "border-[#C5A059]/40 shadow-lg shadow-[#C5A059]/10"
                      : "border-white/10"
                  )}
                >
                  {/* Glow pulse on active */}
                  {isOpen && (
                    <div className="pointer-events-none absolute inset-0 rounded-2xl bg-[#C5A059]/5" />
                  )}

                  <button
                    type="button"
                    onClick={() => setMobileActive(isOpen ? -1 : i)}
                    className="flex w-full items-center gap-4 text-left outline-none"
                    data-testid={`process-step-mobile-${i + 1}`}
                  >
                    <div
                      className={cn(
                        "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition-all",
                        isOpen ? s.badgeColor : "border-white/20 text-neutral-300 bg-white/5"
                      )}
                    >
                      <s.icon className={cn("h-5 w-5", isOpen ? s.accentColor : "")} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-neutral-300">
                          {s.phase}
                        </span>
                        <span
                          className={cn(
                            "rounded-full border px-2 py-0.5 text-[10px] font-semibold",
                            isOpen ? s.badgeColor : "border-white/20 text-neutral-300 bg-white/5"
                          )}
                        >
                          {s.timeline}
                        </span>
                      </div>
                      <span className={cn("font-heading text-base font-semibold", isOpen ? "text-white" : "text-muted-foreground")}>
                        {s.title}
                      </span>
                    </div>
                    <span aria-hidden="true" className={cn("font-heading text-3xl font-light opacity-30 select-none", isOpen ? s.accentColor : "text-white")}>
                      {s.number}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="mt-5 border-t border-white/10 pt-5 space-y-4">
                      <p className="text-sm leading-relaxed text-muted-foreground">{s.description}</p>
                      <div className="flex flex-col gap-2.5">
                        {s.points.map((point) => (
                          <div key={point} className="flex items-start gap-3 rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3">
                            <Check className={cn("h-4 w-4 mt-0.5 shrink-0", s.accentColor)} />
                            <span className="text-sm text-foreground/85">{point}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <Reveal delay={200}>
          <div className="mt-14 flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
            <a
              href="/#kontakt"
              className="group inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-8 py-4 text-sm font-semibold text-[#0A0A0A] shadow-lg shadow-[#C5A059]/20 transition-all duration-200 hover:bg-[#b08e4c] hover:shadow-[#C5A059]/30"
            >
              Projekt unverbindlich anfragen
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="https://wa.me/491748367114"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Direkt per WhatsApp anfragen"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:border-emerald-500/50 hover:bg-emerald-500/5 hover:text-emerald-400"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Direkt per WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
